// src/app/pages/t0-dashboard/t0-scenerios/t0-scenerios.component.ts
import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChartModule } from 'primeng/chart';
import { SelectModule } from 'primeng/select';

import { AnalysisStore, MetricValue, GroupValue } from '../../../core/shared/t0-model/analysis.store';
import { YearStore } from '../../../core/shared/t0-model/year.store';
import { combineLatest, map } from 'rxjs';

type XY = { x: number; y: number };

@Component({
  selector: 'app-t0-scenerios',
  standalone: true,
  imports: [CommonModule, FormsModule, ChartModule, SelectModule],
  templateUrl: './t0-scenerios.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class T0SceneriosComponent {
  private store = inject(AnalysisStore);
  private yearStore = inject(YearStore);

  metricOptions: { label: string; value: MetricValue }[] = [
    { label: 'Controlled', value: 'controlled' },
    { label: 'Treated not controlled', value: 'treated_not_controlled' },
    { label: 'Diagnosed', value: 'diagnosed' },
    { label: 'Undiagnosed', value: 'undiagnosed' },
    { label: 'Prevalence', value: 'prevalence' },
    { label: 'Screened', value: 'screened' },
    { label: 'Aware of screening', value: 'aware_of_screening' },
    { label: 'CVD events', value: 'cvd_events' },
    { label: 'CVD patients', value: 'cvd_patients' },
    { label: 'CVD deaths', value: 'cvd_deaths' },
  ];

  groupOptions: { label: string; value: GroupValue }[] = [
    { label: 'Total', value: 'total' },
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' },
    { label: 'Average', value: 'average' },
  ];

  displayRange$ = combineLatest([this.yearStore.displayMinYear$, this.yearStore.year$]).pipe(
    map(([min, max]) => (Number(min) === Number(max) ? String(max) : `${min} to ${max}`))
  );

  get metric(): MetricValue { return this.store.metric$.value; }
  set metric(v: MetricValue) { this.store.metric$.next(v); this.store.onMetricOrGroupChange(); }

  get group(): GroupValue { return this.store.group$.value; }
  set group(v: GroupValue) { this.store.group$.next(v); this.store.onMetricOrGroupChange(); }

  chart$ = combineLatest([this.store.combinedSeries$, this.yearStore.displayMinYear$, this.yearStore.year$]).pipe(
    map(([seriesList, displayMinYear, selectedYear]) => {
      const safeSeries = Array.isArray(seriesList) ? seriesList : [];
      const xMin = Number(displayMinYear);
      const xMax = Number(selectedYear);

      const rangeYears = Math.abs(xMax - xMin);
      const targetTicks = 8;
      const stepForLong = Math.max(1, Math.round(rangeYears / (targetTicks - 1)));

      const baselineColor = '#fec2d6';

      // Expanded palette for simulations
      const simColors = [
        '#d41c58',
        '#7c3aed',
        '#2563eb',
        '#0891b2',
        '#059669',
        '#ca8a04',
        '#ea580c',
        '#be123c',
        '#9333ea',
        '#0f766e'
      ];

      const isBaselineLabel = (s: string) => String(s || '').trim().toLowerCase() === 'basic simulation';

      const baselineSeries = safeSeries.find((s) => isBaselineLabel(String(s.label))) ?? safeSeries[0];

      const makeDs = (label: string, points: XY[], color: string) => ({
        label,
        data: points,
        borderColor: color,
        backgroundColor: 'transparent',
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.3
      });

      const normalizePoints = (pts: any[]) =>
        (pts || [])
          .filter((p: any) => typeof p?.x === 'number' && p.x >= xMin && p.x <= xMax)
          .map((p: any) => ({ x: Number(p.x), y: Number(p.y) }));

      const baselinePoints: XY[] = baselineSeries ? normalizePoints((baselineSeries as any).points) : [];
      const baselineByYear = new Map<number, number>();
      baselinePoints.forEach((p) => baselineByYear.set(Math.round(p.x), Number(p.y)));

      const datasets: any[] = [];
      let simColorIdx = 0;

      // Basic Simulation = solid line
      if (baselineSeries) {
        datasets.push(makeDs(String(baselineSeries.label), baselinePoints, baselineColor));
      }

      // Other simulations = unique colors
      safeSeries
        .filter((s) => s !== baselineSeries)
        .forEach((s: any) => {
          const color = simColors[simColorIdx % simColors.length];
          simColorIdx++;
          datasets.push(makeDs(String(s.label), normalizePoints(s.points), color));
        });

      const data = { datasets };

      const options: any = {
        responsive: true,
        maintainAspectRatio: false,
        parsing: true,
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              color: 'var(--text-color, #334155)',
              usePointStyle: true,
              boxWidth: 10,
              font: { size: 10 }
            }
          },
          tooltip: {
            mode: 'nearest',
            intersect: false,
            callbacks: {
              title: (items: any[]) => `Year: ${String(items[0]?.raw?.x)}`,
              label: (ctx: any) => {
                const year = Math.round(Number(ctx.raw?.x ?? 0));
                const y = Number(ctx.raw?.y ?? 0);
                const label = String(ctx.dataset?.label ?? '');

                const fmt = (n: number) => new Intl.NumberFormat('en-US').format(Math.round(n));

                if (isBaselineLabel(label)) return `${label}: ${fmt(y)}`;

                const base = baselineByYear.get(year);
                if (base === undefined) return `${label}: ${fmt(y)}`;

                const delta = y - base;
                const sign = delta > 0 ? '+' : '';
                const pct = base !== 0 ? (delta / base) * 100 : 0;
                const pctSign = pct > 0 ? '+' : '';
                const pctStr = `${pctSign}${pct.toFixed(1)}%`;

                return `${label}: ${fmt(y)} (Difference with Baseline: ${sign}${fmt(delta)}, ${pctStr})`;
              }
            }
          }
        },
        scales: {
          x: {
            type: 'linear',
            min: xMin,
            max: xMax,
            ticks: {
              stepSize: rangeYears < 10 ? 1 : stepForLong,
              autoSkip: false,
              precision: 0,
              color: 'var(--text-secondary-color, #64748B)',
              callback: (value: number) => String(Math.round(value)),
              font: { size: 10 }
            },
            grid: { color: 'rgba(100,116,139,0.1)' }
          },
          y: {
            beginAtZero: true,
            ticks: {
              color: 'var(--text-secondary-color, #64748B)',
              callback: (v: number) => new Intl.NumberFormat('en-US', { notation: 'compact' }).format(Number(v)),
              font: { size: 10 }
            },
            grid: { color: 'rgba(100,116,139,0.1)' }
          }
        }
      };

      return { data, options };
    })
  );
}